import type { GatewayError } from '@/utils/gatewayError'
import { useMemo } from 'react'
import { filtersTypes } from './types'
import DetailedFilter from './filter-partial/DetailedFilter'
import HourlyFilter from './filter-partial/HourlyFilter'
import DailyFilter from './filter-partial/DailyFilter'
import PeriodicFilter, { type DateRange as PeriodicDateRange } from './filter-partial/PeriodicFilter'
import type { PeriodOption } from '@/services/consumerService'
import { formatReadingsDate } from '@/utils/consumerReadingsDateRanges'

export default function Filters({
  type,
  dailyMinValue,
  dailyMaxValue,
  onDailyJumpMin,
  onDailyJumpMax,
  onPeriodsChange,
  hourlyLoading,
  hourlyTotal,
  hourlyAvg,
  onHourlySearch,
  hourlyRange,
  onHourlyRangeChange,
  dailyData,
  dailyChartData,
  dailyChartLoading,
  dailyChartError,
  dailyLoading,
  dailyTotal,
  dailyAvg,
  dailyNightlyTotal,
  dailyNightlyAvg,
  onDailySearch,
  dailyRange,
  onDailyRangeChange,
  onDailyChartWeekChange,
  periodicData,
  periodicLoading,
  periodicTotal,
  periodicAvg,
  periodicChart,
  onPeriodicSearch,
  periodicRange,
  onPeriodicRangeChange,
  // Detailed props
  detailedData,
  detailedLoading,
  onDetailedSearch,
  detailedRange,
  onDetailedRangeChange,
}: {
  type: filtersTypes
  dailyMinValue?: number | null
  dailyMaxValue?: number | null
  onDailyJumpMin?: () => void
  onDailyJumpMax?: () => void
  onPeriodsChange?: (periods: string) => void
  hourlyLoading?: boolean
  hourlyTotal?: number
  hourlyAvg?: number
  onHourlySearch?: (from?: string, to?: string) => void
  hourlyRange?: { from: Date | null; to: Date | null }
  onHourlyRangeChange?: (range: { from: Date | null; to: Date | null }) => void
  dailyData?: any[]
  dailyChartData?: any[]
  dailyChartLoading?: boolean
  dailyChartError?: GatewayError | null
  dailyLoading?: boolean
  dailyTotal?: number
  dailyAvg?: number
  dailyNightlyTotal?: number
  dailyNightlyAvg?: number
  onDailySearch?: (from?: string, to?: string) => void
  dailyRange?: { from: Date | null; to: Date | null }
  onDailyRangeChange?: (range: { from: Date | null; to: Date | null }) => void
  onDailyChartWeekChange?: (from: string, to: string) => void
  periodicData?: any[]
  periodicLoading?: boolean
  periodicTotal?: number
  periodicAvg?: number
  periodicChart?: { currentYear: number[]; yearAgo: number[] | null }
  onPeriodicSearch?: (from?: string, to?: string, periodOption?: PeriodOption) => void
  periodicRange?: PeriodicDateRange
  onPeriodicRangeChange?: (range: PeriodicDateRange) => void
  // Detailed props
  detailedData?: any[]
  detailedLoading?: boolean
  onDetailedSearch?: (from?: string, to?: string) => void
  detailedRange?: { from: Date | null; to: Date | null }
  onDetailedRangeChange?: (range: { from: Date | null; to: Date | null }) => void
}) {
  return useMemo(() => {
    switch (type) {
      case filtersTypes.hourly:
        return (
          <HourlyFilter
            range={hourlyRange}
            onRangeChange={onHourlyRangeChange}
            loading={hourlyLoading}
            totalConsumption={hourlyTotal}
            avgConsumption={hourlyAvg}
            onSearch={onHourlySearch}
          />
        )
      case filtersTypes.daily:
        return (
          <DailyFilter
            range={dailyRange}
            onRangeChange={onDailyRangeChange}
            minValue={dailyMinValue ?? null}
            maxValue={dailyMaxValue ?? null}
            onJumpMin={onDailyJumpMin}
            onJumpMax={onDailyJumpMax}
            data={dailyData}
            chartData={dailyChartData}
            chartLoading={dailyChartLoading}
            chartError={dailyChartError}
            loading={dailyLoading}
            totalConsumption={dailyTotal}
            avgConsumption={dailyAvg}
            totalNightlyConsumption={dailyNightlyTotal}
            avgNightlyConsumption={dailyNightlyAvg}
            onSearch={onDailySearch}
            onChartWeekChange={onDailyChartWeekChange}
          />
        )
      case filtersTypes.periodic:
        return (
          <PeriodicFilter
            range={periodicRange}
            onRangeChange={onPeriodicRangeChange}
            onPeriodsChange={onPeriodsChange}
            data={periodicData}
            loading={periodicLoading}
            totalConsumption={periodicTotal}
            avgConsumption={periodicAvg}
            chartData={periodicChart}
            onSearch={onPeriodicSearch}
          />
        )
      case filtersTypes.detailed:
        return (
          <DetailedFilter
            range={detailedRange}
            onRangeChange={onDetailedRangeChange}
            loading={detailedLoading}
            onSearch={onDetailedSearch ? (range) => {
              const fromStr = range.from ? formatReadingsDate(range.from) : undefined
              const toStr = range.to ? formatReadingsDate(range.to) : undefined
              onDetailedSearch(fromStr, toStr)
            } : undefined}
          />
        )
      default:
        return null
    }
  }, [type, dailyMinValue, dailyMaxValue, onDailyJumpMin, onDailyJumpMax, onPeriodsChange, hourlyLoading, hourlyTotal, hourlyAvg, onHourlySearch, hourlyRange, onHourlyRangeChange, dailyData, dailyChartData, dailyChartLoading, dailyChartError, dailyLoading, dailyTotal, dailyAvg, dailyNightlyTotal, dailyNightlyAvg, onDailySearch, dailyRange, onDailyRangeChange, onDailyChartWeekChange, periodicData, periodicLoading, periodicTotal, periodicAvg, periodicChart, onPeriodicSearch, periodicRange, onPeriodicRangeChange, detailedData, detailedLoading, onDetailedSearch, detailedRange, onDetailedRangeChange])
}
